import { Button, Checkbox, Form, Modal, Tooltip } from "antd";
import { isEqual, sortBy } from "lodash-es";
import { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ModalDivider } from "~components/ModalDivider/ModalDivider.tsx";
import { formStyles } from "~pages/Fixtures/FixtureEditPage/FixtureEditPage.styles";
import type { DefaultOperationSuccessResponse } from "~types/response";
import { sortStringValues } from "~utils/utilities.ts";
import { useNotificationCount } from "~context/NotificationCountContext/NotificationCountContext";

type Props = {
  handleExport: (fieldNames: Array<string>) => Promise<DefaultOperationSuccessResponse>;
  groupedFields: Array<{ fields: Array<string>; titleIntlKey: string }>;
  defaultFields: Array<string>;
  localStorageKey: string;
  loading: boolean;
};

export const ExportModal = ({
  handleExport,
  groupedFields,
  defaultFields,
  localStorageKey,
  loading,
}: Props) => {
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { refetchCount } = useNotificationCount();

  const [form] = Form.useForm();

  // It is important to keep the sequence of fields, because the column sorting of resulting csv fill be based on this
  const allFields = groupedFields.flatMap((field) => field.fields);

  const handleOk = useCallback(async () => {
    const { fields } = await form.validateFields();
    const sortedFields = fields.sort((a: string, b: string) => allFields.indexOf(a) - allFields.indexOf(b));
    localStorage.setItem(localStorageKey, JSON.stringify(sortedFields));
    await handleExport(sortedFields).then(() => {
      setOpen(false);
    });
    setTimeout(() => {
      refetchCount();
    }, 3000);
  }, [form]);

  const [isSelectAllChecked, setSelectAllChecked] = useState(false);

  return (
    <>
      <Modal
        open={open}
        width={800}
        onCancel={() => {
          setOpen(false);
          setSelectAllChecked(false);
          form.resetFields();
        }}
        afterOpenChange={(visible) => {
          if (visible) {
            const savedFields = JSON.parse(localStorage.getItem(localStorageKey) || "null");
            const allFieldsSelected = isEqual(sortBy(savedFields || defaultFields), sortBy(allFields));
            form.setFieldsValue({ fields: savedFields || defaultFields });
            setSelectAllChecked(allFieldsSelected);
          }
        }}
        onOk={handleOk}
        okText={formatMessage({ id: "export" })}
        okButtonProps={{ loading: loading }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <div style={{ display: "flex", gap: 5, justifyContent: "end" }}>
            <Button
              onClick={() => {
                setSelectAllChecked(false);
                form.setFieldsValue({ fields: defaultFields });
              }}
              style={{ marginRight: "auto" }}
            >
              Default
            </Button>
            <CancelBtn />
            <OkBtn />
          </div>
        )}
      >
        <h5>
          <FormattedMessage id="export.modal.title" />
        </h5>
        <div style={{ marginBottom: "10px" }}>{formatMessage({ id: "export.modal.description" })}</div>
        <Form
          form={form}
          style={formStyles}
          onValuesChange={() => {
            const data = form.getFieldsValue(true);
            const formFieldsValues: Array<string> = data?.fields || [];
            const allFieldsSelected = isEqual(sortBy(formFieldsValues), sortBy(allFields));

            setSelectAllChecked(allFieldsSelected);
          }}
        >
          <Form.Item style={{ marginBottom: 0 }}>
            <Checkbox
              checked={isSelectAllChecked}
              onChange={(e) => {
                const isChecked = e.target.checked;
                setSelectAllChecked(isChecked);
                const values = isChecked ? allFields : [];
                form.setFieldsValue({ fields: values });
              }}
            >
              <FormattedMessage id="select.all" />
            </Checkbox>
          </Form.Item>
          <Form.Item name="fields" rules={[{ required: true, message: formatMessage({ id: "export.form.error" }) }]}>
            <Checkbox.Group style={{ width: "100%" }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", width: "100%" }}>
                {[...groupedFields]
                  .sort((a, b) =>
                    sortStringValues(formatMessage({ id: a.titleIntlKey }), formatMessage({ id: b.titleIntlKey })),
                  )
                  .map((field) => (
                    <div
                      key={field.titleIntlKey}
                      style={{ display: "flex", flexDirection: "column", gap: "4px", width: "100%" }}
                    >
                      <ModalDivider translationKey={field.titleIntlKey} />
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                        {[...field.fields].sort(sortStringValues).map((field) => (
                          <div
                            key={field}
                            style={{
                              flex: "0 0 24%",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox value={field}>
                              <FormattedMessage id={`export.field.${field}`} />
                            </Checkbox>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            </Checkbox.Group>
          </Form.Item>
        </Form>
      </Modal>
      <Tooltip placement="topLeft" title={formatMessage({ id: "export" })} arrow>
        <Button
          onClick={() => {
            setOpen(true);
          }}
          type={"text"}
          icon={<i className="fa-solid fa-file-text"></i>}
        ></Button>
      </Tooltip>
    </>
  );
};
